import type { ServiceRow } from '@/hooks/useServices';
import type { TreatmentCourseTemplateDraft } from '@/types/treatmentCourses';

export type AppointmentTypeCatalogKind = 'service' | 'course';

export type AppointmentTypeCatalogRow = {
  key: string;
  kind: AppointmentTypeCatalogKind;
  id: string;
  serviceId: string | null;
  templateId: string | null;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
  duration_minutes: number | null;
  price_cents: number | null;
  currency: string;
  stepCount: number;
  href: string;
};

export const APPOINTMENT_TYPES_LIST_PATH = '/settings/services';

export function appointmentTypeServicePath(serviceId: string): string {
  return `${APPOINTMENT_TYPES_LIST_PATH}/${serviceId}`;
}

export function appointmentTypeCoursePath(templateId: string): string {
  return `${APPOINTMENT_TYPES_LIST_PATH}/course/${templateId}`;
}

export function formatSingleTypeSummary(durationMinutes: number, priceCents: number): string {
  const kroner = Math.round(priceCents / 100);
  return `${durationMinutes} min · ${kroner} kr.`;
}

export function isMultiStepType(stepCount: number): boolean {
  return stepCount > 1;
}

export function bookableCourseTemplates(
  templates: TreatmentCourseTemplateDraft[] | undefined,
): TreatmentCourseTemplateDraft[] {
  return (templates ?? []).filter((row) => row.is_active && row.steps.length > 1);
}

/**
 * Patient appointment types on Indstillinger → Aftaletyper and in Ny aftale → Aftaletype.
 * Calendar block reasons are clinic services with is_blocker and stay on Bloker tid.
 */
export function isPatientAppointmentType(service: { is_blocker?: boolean | null }): boolean {
  return !service.is_blocker;
}

export function buildAppointmentTypeCatalog(args: {
  services: ServiceRow[] | undefined;
  templates: TreatmentCourseTemplateDraft[] | undefined;
}): AppointmentTypeCatalogRow[] {
  const services = args.services ?? [];
  const templates = args.templates ?? [];
  const byCatalogService = new Map<string, TreatmentCourseTemplateDraft>();
  const standalone: TreatmentCourseTemplateDraft[] = [];

  for (const template of templates) {
    if (template.catalog_service_id) {
      byCatalogService.set(template.catalog_service_id, template);
    } else if (template.id) {
      standalone.push(template);
    }
  }

  const rows: AppointmentTypeCatalogRow[] = [];

  for (const service of services) {
    if (!isPatientAppointmentType(service)) continue;
    const template = byCatalogService.get(service.id);
    const stepCount = template?.steps.length ?? 1;
    rows.push({
      key: `service:${service.id}`,
      kind: 'service',
      id: service.id,
      serviceId: service.id,
      templateId: template?.id ?? null,
      name: service.name,
      description: service.description ?? '',
      color: service.color,
      is_active: service.is_active,
      duration_minutes: service.duration_minutes,
      price_cents: service.price_cents,
      currency: service.currency,
      stepCount,
      href: appointmentTypeServicePath(service.id),
    });
  }

  for (const template of standalone) {
    const firstStep = template.steps[0];
    const firstService = firstStep
      ? services.find((svc) => svc.id === firstStep.service_id)
      : undefined;
    rows.push({
      key: `course:${template.id}`,
      kind: 'course',
      id: template.id ?? template.name,
      serviceId: null,
      templateId: template.id ?? null,
      name: template.name,
      description: template.description ?? '',
      color: template.color,
      is_active: template.is_active,
      duration_minutes: firstStep?.duration_minutes ?? firstService?.duration_minutes ?? null,
      price_cents: template.package_price_cents ?? firstService?.price_cents ?? null,
      currency: firstService?.currency ?? 'DKK',
      stepCount: template.steps.length,
      href: template.id ? appointmentTypeCoursePath(template.id) : APPOINTMENT_TYPES_LIST_PATH,
    });
  }

  return rows.sort((a, b) => a.name.localeCompare(b.name, 'da'));
}

export function catalogRowSummary(
  row: AppointmentTypeCatalogRow,
  stepCountLabel: (count: number) => string,
): string {
  if (isMultiStepType(row.stepCount)) return stepCountLabel(row.stepCount);
  if (row.duration_minutes != null && row.price_cents != null) {
    return formatSingleTypeSummary(row.duration_minutes, row.price_cents);
  }
  if (row.duration_minutes != null) return `${row.duration_minutes} min`;
  return stepCountLabel(row.stepCount);
}
